import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { type Board, type ListResponse } from '@air/api/types';

import { getLibraryRootBoardsKey } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { getPrivateBoardsListKey } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useFavoriteBoardsParams } from '~/components/v2/hooks/useFavoriteBoardsParams';
import { GALLERY_BOARDS } from '~/constants/react-query-keys';
import { getFavoriteBoardsKey } from '~/swr-hooks/boards/useFavoriteBoardsList';
import { BOARD_SUBBOARDS, getBoardSubBoardsKey } from '~/swr-hooks/boards/useSubBoardsList';
import { getWorkspaceBoardsKey } from '~/swr-hooks/boards/useWorkspaceBoards';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import { getUpdatedBoard } from '~/utils/BoardUtils';
import { getUpdatedBoards } from '~/utils/mutateUtils/mutators';
import { addBoards, removeBoards } from '~/utils/mutateUtils/SideNavBoards';

export type UpdateBoardFavoriteStatusInAllCachesParams = {
  board: Board;
  newHasCurrentUser: boolean;
};

/**
 * Hook to update board favorite status in all caches:
 * - Favorite boards list
 * - Collection gallery boards
 * - Side nav queries (sub-boards, library root boards, workspace root boards)
 */
export const useUpdateBoardFavoriteStatusInAllCaches = () => {
  const queryClient = useQueryClient();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { getFavoriteBoardsParams } = useFavoriteBoardsParams();

  const updateBoardFavoriteStatusInAllCaches = useCallback(
    ({ board, newHasCurrentUser }: UpdateBoardFavoriteStatusInAllCachesParams) => {
      const updatedBoard = { ...board, hasCurrentUser: newHasCurrentUser };
      const libraryId = board.library?.id;
      const subNavSort = getSubNavSortValue(libraryId);

      // Invalidate favorite boards list query (for usePrivateBoardsList with favorite params)
      queryClient.invalidateQueries({ queryKey: getPrivateBoardsListKey(getFavoriteBoardsParams()) });

      // Update favorite boards list - add if favoriting, remove if unfavoriting
      const favoriteBoardsKey = getFavoriteBoardsKey(board.workspaceId);
      if (newHasCurrentUser) {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          { queryKey: favoriteBoardsKey },
          addBoards([updatedBoard]),
        );
      } else {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          { queryKey: favoriteBoardsKey },
          removeBoards([board.id]),
        );
      }

      // Update collection gallery boards queries (boards may appear in collections)
      const galleryQueries = queryClient.getQueriesData({
        predicate: (query) => {
          return Array.isArray(query.queryKey) && query.queryKey[0] === GALLERY_BOARDS;
        },
      });

      galleryQueries.forEach(([queryKey]) => {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey }, (data) => {
          if (!data) {
            return undefined;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              data: page.data.map((b) =>
                b.id === board.id ? getUpdatedBoard({ newBoard: updatedBoard, oldBoard: b }) : b,
              ),
            })),
          };
        });
      });

      // Update queries based on board location
      if (board.parentId) {
        // Board is a sub-board - update parent's sub-boards query
        const sortField = libraryId ? subNavSort?.libraryBoardSorts?.[0] : subNavSort?.boardSort;
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          { queryKey: getBoardSubBoardsKey(board.parentId, sortField, libraryId) },
          getUpdatedBoards(updatedBoard),
        );
      } else if (libraryId) {
        // Board is a root library board - update library root boards query
        const librarySort = subNavSort?.libraryBoardSorts?.[0];
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          {
            queryKey: getLibraryRootBoardsKey({
              workspaceId: board.workspaceId,
              libraryId,
              sortField: librarySort,
            }),
          },
          getUpdatedBoards(updatedBoard),
        );
      } else {
        // Board is a root workspace board - update root workspace boards query
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
          {
            queryKey: getWorkspaceBoardsKey({
              workspaceId: board.workspaceId,
              sortField: subNavSort?.boardSort,
            }),
          },
          getUpdatedBoards(updatedBoard),
        );
      }

      // Update all sub-boards queries (board may appear as a sub-board in various parent boards)
      queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
        { queryKey: [BOARD_SUBBOARDS] },
        getUpdatedBoards(updatedBoard),
      );
    },
    [getFavoriteBoardsParams, getSubNavSortValue, queryClient],
  );

  return {
    updateBoardFavoriteStatusInAllCaches,
  };
};
